import { HttpClient, HttpEvent, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { APP_CONFIG } from '@application/config';
import { withoutForbiddenElevation } from '@application/data-access';
import { fromWireEnums } from '../entities/academy-enums.model';
import {
  DiscoverQuery,
  MineCoursesQuery,
  ODataQuery,
} from '../entities/academy-query.model';
import {
  CourseEntity,
  CourseItemEntity,
  ODataList,
} from '../entities/academy-entities.model';
import {
  CourseCreateRequest,
  CourseResponse,
  CourseUpdateRequest,
} from '../entities/academy.model';
import {
  CourseAttachmentResponse,
  CourseItemCreatedResponse,
  CourseItemUploadResponse,
  CourseProgressResponse,
  CoverResponse,
  MineResult,
  MyCourseSummary,
  PlaybackTokenResponse,
  toMineResult,
} from '../entities/academy-responses.model';
import { AcademyCrudService } from './academy-crud.service';
import { AcademyODataService } from './academy-odata.service';

/**
 * HTTP client for the Courses area custom endpoints (`/Courses/*`).
 * Source of truth: `docs/academy-api-integration.md` §7.8. Base reads over `Courses`
 * use `AcademyODataService` (OData list); writes/by-id use `AcademyCrudService`.
 */
@Injectable({ providedIn: 'root' })
export class CoursesService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(APP_CONFIG).apiUrl.academy_service;
  private readonly odata = inject(AcademyODataService);
  private readonly crud = inject(AcademyCrudService);

  /**
   * `GET /Courses/mine` — courses the student is consuming (has VOD activity).
   * Ordered by most-recently-watched first. `status` defaults to `all`.
   */
  public mine(
    query: MineCoursesQuery = {}
  ): Observable<MineResult<MyCourseSummary>> {
    return this.http
      .get<unknown>(`${this.baseUrl}/Courses/mine`, {
        params: this.buildMineParams(query),
        withCredentials: true,
      })
      .pipe(map((res) => toMineResult<MyCourseSummary>(res)));
  }

  /** `GET /Courses/discover` — published courses visible to the viewer. Plain array. */
  public discover(query: DiscoverQuery = {}): Observable<CourseResponse[]> {
    return this.http
      .get<CourseResponse[]>(`${this.baseUrl}/Courses/discover`, {
        params: this.buildDiscoverParams(query),
        withCredentials: true,
      })
      .pipe(map((items) => fromWireEnums(items)));
  }

  /**
   * `POST /Courses/{courseId}/items/{itemId}/playbackToken` — signed token for a course item.
   * 🔑 identity from token: empty body, never a `PersonId`. ⚠ 403 outside audience — marked
   * `withoutForbiddenElevation()`, since elevating to another user of the same tenant never
   * puts that tenant in the item's audience (mirror of `LiveEventsService.playbackToken`).
   */
  public itemPlaybackToken(
    courseId: string,
    itemId: string
  ): Observable<PlaybackTokenResponse> {
    return this.http.post<PlaybackTokenResponse>(
      `${this.baseUrl}/Courses/${encodeURIComponent(
        courseId
      )}/items/${encodeURIComponent(itemId)}/playbackToken`,
      {},
      { context: withoutForbiddenElevation(), withCredentials: true }
    );
  }

  /**
   * `POST /Courses/{id}/items` — attach a Recorded live event as a course item.
   * ⚠ non-Recorded / missing event → 4xx/5xx.
   */
  public addItem(
    courseId: string,
    liveEventId: string,
    order: number,
    isRequired = true
  ): Observable<CourseItemCreatedResponse> {
    const params = new HttpParams()
      .set('liveEventId', liveEventId)
      .set('order', String(order))
      .set('isRequired', String(isRequired));
    return this.http.post<CourseItemCreatedResponse>(
      `${this.baseUrl}/Courses/${encodeURIComponent(courseId)}/items`,
      {},
      { params, withCredentials: true }
    );
  }

  /**
   * `POST /Courses/{id}/cover` — multipart cover image (field `file`). The API forwards
   * the binary to the StorageService and answers with the stored `CoverUrl`. The browser
   * sets the multipart boundary; the interceptor skips `Content-Type` for `FormData`.
   */
  public setCover(courseId: string, file: File): Observable<CoverResponse> {
    const form = new FormData();
    form.append('file', file); // File carries its own name → multipart filename is preserved
    return this.http.post<CoverResponse>(
      `${this.baseUrl}/Courses/${encodeURIComponent(courseId)}/cover`,
      form,
      { withCredentials: true }
    );
  }

  /** `POST /Courses/{id}/attachments` — multipart support material (field `file`). */
  public addAttachment(
    courseId: string,
    file: File
  ): Observable<CourseAttachmentResponse> {
    const form = new FormData();
    form.append('file', file);
    return this.http.post<CourseAttachmentResponse>(
      `${this.baseUrl}/Courses/${encodeURIComponent(courseId)}/attachments`,
      form,
      { withCredentials: true }
    );
  }

  /** `GET /Courses/{id}/attachments` — gated list of the course's support materials. */
  public listAttachments(
    courseId: string
  ): Observable<CourseAttachmentResponse[]> {
    return this.http.get<CourseAttachmentResponse[]>(
      `${this.baseUrl}/Courses/${encodeURIComponent(courseId)}/attachments`,
      { withCredentials: true }
    );
  }

  /** `DELETE /Courses/{courseId}/attachments/{attachmentId}` — remove a support material. */
  public removeAttachment(
    courseId: string,
    attachmentId: string
  ): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/Courses/${encodeURIComponent(
        courseId
      )}/attachments/${encodeURIComponent(attachmentId)}`,
      { withCredentials: true }
    );
  }

  /** `DELETE /Courses/{courseId}/items/{itemId}` — soft-delete a course item (lesson). */
  public removeItem(courseId: string, courseItemId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/Courses/${encodeURIComponent(
        courseId
      )}/items/${encodeURIComponent(courseItemId)}`,
      { withCredentials: true }
    );
  }

  /** `PATCH /CourseItems?id={guid}` — update course item details (generic OData update). */
  public updateItem(
    courseItemId: string,
    viewModel: Partial<CourseItemEntity>
  ): Observable<CourseItemEntity> {
    return this.crud.update<CourseItemEntity>(
      'CourseItems',
      courseItemId,
      viewModel
    );
  }

  /**
   * `PATCH /Courses/{courseId}/items/reorder` — bulk-atomic reorder; `orderedIds` is the full
   * set of **CourseItemId**s in the desired order (`Order = index`). A mismatched set → 400.
   */
  public reorderItems(
    courseId: string,
    orderedIds: string[]
  ): Observable<void> {
    return this.http.patch<void>(
      `${this.baseUrl}/Courses/${encodeURIComponent(courseId)}/items/reorder`,
      { OrderedIds: orderedIds }, // PascalCase: backend `ReorderRequest(IReadOnlyList<Guid> OrderedIds)`
      { withCredentials: true }
    );
  }

  /** `PATCH /Courses/{courseId}/items/{itemId}/required?isRequired=…` — toggle the required flag. */
  public setItemRequired(
    courseId: string,
    courseItemId: string,
    isRequired: boolean
  ): Observable<void> {
    const params = new HttpParams().set('isRequired', String(isRequired));
    return this.http.patch<void>(
      `${this.baseUrl}/Courses/${encodeURIComponent(
        courseId
      )}/items/${encodeURIComponent(courseItemId)}/required`,
      {},
      { params, withCredentials: true }
    );
  }

  /** `POST /Courses/{id}/publish` — publish the course (requires ≥1 item). ⚠404. */
  public publish(courseId: string): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/Courses/${encodeURIComponent(courseId)}/publish`,
      {},
      { withCredentials: true }
    );
  }

  /**
   * `POST /Courses/{courseId}/items/{itemId}/progress` — report watched seconds.
   * 🔑 identity from token. Always 200 (even for a missing item).
   */
  public itemProgress(
    courseId: string,
    itemId: string,
    positionSeconds: number
  ): Observable<void> {
    const params = new HttpParams().set(
      'positionSeconds',
      String(positionSeconds)
    );
    return this.http.post<void>(
      `${this.baseUrl}/Courses/${encodeURIComponent(
        courseId
      )}/items/${encodeURIComponent(itemId)}/progress`,
      {},
      { params, withCredentials: true }
    );
  }

  /** `GET /Courses/{id}/progress` — current viewer's course progress. 🔑 */
  public progress(courseId: string): Observable<CourseProgressResponse> {
    return this.http.get<CourseProgressResponse>(
      `${this.baseUrl}/Courses/${encodeURIComponent(courseId)}/progress`,
      { withCredentials: true }
    );
  }

  /** `POST /Courses` — create a course (generic OData create; `CourseId` is server-assigned). */
  public create(viewModel: CourseCreateRequest): Observable<CourseResponse> {
    return this.crud.create<CourseResponse>('Courses', viewModel);
  }

  /** `PATCH /Courses?id={guid}` — update a course (field-by-field merge; omitted fields keep their value). */
  public update(
    courseId: string,
    viewModel: CourseUpdateRequest
  ): Observable<CourseResponse> {
    return this.crud.update<CourseResponse>('Courses', courseId, viewModel);
  }

  /**
   * `GET /Courses?id={guid}` — read one course by key. Unlike `discover`/`list`, the single
   * read carries `CoverUrl`, so the edit builder can show the persisted cover.
   */
  public getById(courseId: string): Observable<CourseResponse | null> {
    return this.crud.getById<CourseResponse>('Courses', courseId);
  }

  /** `DELETE /Courses?id={guid}` — remove a course (OData delete). */
  public remove(courseId: string): Observable<void> {
    return this.crud.delete('Courses', courseId);
  }

  /**
   * `OPTIONS /Courses` — producer-side OData list (drafts included, unlike `discover`).
   * Returns the ENTITY shape (§6.4): supports `$expand=Items` for the course's lessons.
   */
  public list(query: ODataQuery = {}): Observable<ODataList<CourseEntity>> {
    return this.odata.list<CourseEntity>('Courses', query);
  }

  /**
   * `GET /OData/CourseItems` — enriched item list for a course, ordered by `Order`.
   * Unlike `list({ expand: 'Items' })`, this set returns lesson metadata directly
   * (`Title`, `DurationSeconds`, `LiveEventStatusType`) without a separate LiveEvents read.
   * Served at `/OData/` path with standard GET (not the `OPTIONS /{Set}` convention).
   */
  public listItems(courseId: string): Observable<ODataList<CourseItemEntity>> {
    return this.http
      .get<ODataList<CourseItemEntity>>(`${this.baseUrl}/OData/CourseItems`, {
        params: new HttpParams()
          .set('$filter', `CourseId eq ${courseId}`)
          .set('$orderby', 'Order'),
        withCredentials: true,
      })
      .pipe(map((res) => ({ ...res, value: fromWireEnums(res.value) })));
  }

  /**
   * `GET /OData/CourseItems?$orderby=Order` — all items across all courses (no filter).
   * Used by producer list page to fill `itemsByCourse` without `$expand=Items` on Courses.
   */
  public listAllItems(): Observable<ODataList<CourseItemEntity>> {
    return this.http
      .get<ODataList<CourseItemEntity>>(`${this.baseUrl}/OData/CourseItems`, {
        params: new HttpParams().set('$orderby', 'Order'),
        withCredentials: true,
      })
      .pipe(map((res) => ({ ...res, value: fromWireEnums(res.value) })));
  }

  /**
   * `POST /Courses/{id}/items/upload` — VOD upload handshake:
   * creates the VOD `LiveEvent` + the `CourseItem`, and returns a one-time
   * Cloudflare Direct Creator Upload URL.
   * Returns `VideoAssetId` (not `LiveEventId`; the upload creates a `VideoAsset`).
   */
  public uploadItem(
    courseId: string,
    title: string,
    order = 0,
    isRequired = true,
    description?: string | null
  ): Observable<CourseItemUploadResponse> {
    let params = new HttpParams()
      .set('title', title)
      .set('order', String(order))
      .set('isRequired', String(isRequired));
    if (description != null && description.trim() !== '') {
      params = params.set('description', description.trim());
    }
    return this.http
      .post<Record<string, unknown>>(
        `${this.baseUrl}/Courses/${encodeURIComponent(courseId)}/items/upload`,
        {},
        { params, withCredentials: true }
      )
      .pipe(map((res) => this.normalizeUploadResponse(res)));
  }

  /**
   * Direct Creator Upload — POST the binary straight to the one-time Cloudflare URL
   * (form-data, ≤200 MB; larger files need the tus protocol, not implemented).
   * Deliberately WITHOUT `withCredentials`: the `TokenInterceptor` gates on that flag,
   * so this cross-origin request goes out untouched (no Bearer header to trip CORS).
   * Emits `HttpEvent`s so callers can surface upload progress.
   */
  public uploadVideoBinary(
    uploadUrl: string,
    file: File
  ): Observable<HttpEvent<unknown>> {
    const form = new FormData();
    form.append('file', file);
    return this.http.post(uploadUrl, form, {
      reportProgress: true,
      observe: 'events',
    });
  }

  private buildDiscoverParams(query: DiscoverQuery): HttpParams {
    let params = new HttpParams();
    // App scope is resolved server-side from the user's account (feature 010) — never send
    // `?product=`. `query.product` is kept on the type as accepted-but-ignored until the
    // admin manual filter UI is retired.
    if (query.search) params = params.set('search', query.search);
    return params;
  }

  private buildMineParams(query: MineCoursesQuery): HttpParams {
    let params = new HttpParams();
    if (query.status) params = params.set('status', query.status);
    if (query.top != null) params = params.set('top', String(query.top));
    if (query.skip != null) params = params.set('skip', String(query.skip));
    return params;
  }

  /**
   * The upload handshake answers with camelCase keys (Cloudflare Direct Creator
   * Upload style), unlike the PascalCase §6.3 convention used elsewhere. Read
   * either casing and return the PascalCase `CourseItemUploadResponse` the
   * assembly/upload flows consume (`handshake.UploadUrl`).
   */
  private normalizeUploadResponse(
    res: Record<string, unknown>
  ): CourseItemUploadResponse {
    return {
      CourseItemId: (res['CourseItemId'] ?? res['courseItemId']) as string,
      VideoAssetId: (res['VideoAssetId'] ?? res['videoAssetId']) as string,
      UploadUrl: (res['UploadUrl'] ?? res['uploadUrl']) as string,
    };
  }
}
